import { useEffect, useId, useRef, type ReactNode } from "react";
import { Button } from "@astryxdesign/core/Button";
import * as stylex from "@stylexjs/stylex";
import { styles } from "../lib/styles";
export function PageHeader({
  title,
  description,
  action
}: {
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return <div {...stylex.props(styles.pageHeading)}>
      <div>
        <h1 {...stylex.props(styles.title)}>{title}</h1>
        <p {...stylex.props(styles.description)}>{description}</p>
      </div>
      {action && <div className="page-action">{action}</div>}
    </div>;
}
export function Stat({
  label,
  value,
  detail,
  tone
}: {
  label: string;
  value: ReactNode;
  detail: string;
  tone?: "success" | "warning";
}) {
  return <div className="stat-card">
      <span className="stat-label">{label}</span>
      <strong className={tone ? `stat-value ${tone}` : "stat-value"}>{value}</strong>
      <span {...stylex.props(styles.small)}>{detail}</span>
    </div>;
}
export function SectionTabs<T extends string>({
  tabs,
  value,
  onChange
}: {
  tabs: {
    id: T;
    label: string;
    count?: number;
  }[];
  value: T;
  onChange: (value: T) => void;
}) {
  return <nav className="section-tabs" aria-label="Page sections">
      {tabs.map(tab => <button key={tab.id} type="button" aria-current={tab.id === value ? "page" : undefined} onClick={() => onChange(tab.id)}>
          {tab.label}
          {tab.count !== undefined && <span>{tab.count}</span>}
        </button>)}
    </nav>;
}
export function Drawer({
  title,
  description,
  children,
  onClose
}: {
  title: string;
  description?: string;
  children: ReactNode;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  useEffect(() => {
    const dialog = ref.current;
    const previous = document.activeElement as HTMLElement | null;
    dialog?.showModal();
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      dialog?.close();
      document.body.style.overflow = overflow;
      previous?.focus();
    };
  }, []);
  return <dialog ref={ref} className="workspace-drawer" aria-labelledby={titleId} aria-describedby={description ? descriptionId : undefined} onCancel={event => {
    event.preventDefault();
    onClose();
  }}>
      <div className="drawer-heading">
        <div>
          <h2 id={titleId}>{title}</h2>
          {description && <p id={descriptionId} {...stylex.props(styles.description)}>
              {description}
            </p>}
        </div>
        <Button label="Close" size="sm" onClick={onClose} />
      </div>
      <div className="drawer-content">{children}</div>
    </dialog>;
}
export function EmptyState({
  title,
  description,
  action
}: {
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return <div className="empty-state">
      <span className="empty-symbol" aria-hidden="true">
        <NavIcon name="Servers" />
      </span>
      <h3>{title}</h3>
      <p {...stylex.props(styles.description)}>{description}</p>
      {action}
    </div>;
}
export function SearchField({
  value,
  onChange,
  label = "Search",
  placeholder
}: {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder: string;
}) {
  return <label className="search-field">
      <span className="sr-only">{label}</span>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
        <circle cx="10.5" cy="10.5" r="6.5" />
        <path d="m16 16 5 5" />
      </svg>
      <input type="search" value={value} placeholder={placeholder} onChange={event => onChange(event.target.value)} />
    </label>;
}
export function Pagination({
  page,
  total,
  size = 15,
  onChange
}: {
  page: number;
  total: number;
  size?: number;
  onChange: (value: number) => void;
}) {
  const pages = Math.max(1, Math.ceil(total / size));
  return <div className="table-footer">
      <span>
        {total ? `${page * size + 1}–${Math.min((page + 1) * size, total)} of ${total}` : "0 results"}
      </span>
      <div className="pagination">
        <Button label="Previous" size="sm" isDisabled={page === 0} onClick={() => onChange(page - 1)} />
        <span>
          Page {page + 1} of {pages}
        </span>
        <Button label="Next" size="sm" isDisabled={page + 1 >= pages} onClick={() => onChange(page + 1)} />
      </div>
    </div>;
}
export function NavIcon({
  name
}: {
  name: string;
}) {
  const paths: Record<string, string> = {
    Overview: "M3 3h7v7H3z M14 3h7v7h-7z M3 14h7v7H3z M14 14h7v7h-7z",
    Servers: "M4 3h16v7H4z M4 14h16v7H4z M7 6h1 M7 17h1",
    Cloud: "M7 18a5 5 0 0 1-1-10 6 6 0 0 1 11-2 6 6 0 1 1 1 12H7",
    Access: "M12 3 3 7v5c0 5 9 9 9 9s9-4 9-9V7z M8 12l3 3 5-6",
    Recordings: "M4 3h16v18H4z M10 8l6 4-6 4z",
    Activity: "M3 12h4l3-8 4 16 3-8h4",
    Connect: "M8 4 2 10l6 6 M16 8l6 6-6 6 M14 3l-4 18",
    ArrowLeft: "M20 12H4m6-6-6 6 6 6",
    ArrowRight: "M4 12h16m-6-6 6 6-6 6"
  };
  return <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={paths[name] ?? paths.Servers} />
    </svg>;
}